/** Angular imports */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/** Interfaces */
import {
    ApiDashboardContents,
    ApiDashboardDealers,
    ApiDashboardGlobal,
    ApiDashboardHosts,
    ApiDashboardLicenses,
    ApiDashboardRaspi,
    ApiDashboardUsers,
} from './dashboard.interfaces';

/**
 * Service responsible for managing dashboard statistics and data.
 *
 * @description
 * Handles API interactions for dashboard endpoints including dealers,
 * licenses, users, hosts, contents, and global statistics. Implements
 * caching for expensive queries using the SharedService.
 */
@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    /**
     * Inject http instance for api calls
     */
    private readonly _http = inject(HttpClient);

    /** Base endpoint for the dashboard routes */
    private readonly _baseUrl = '/api/dashboard';

    /** Endpoint for dealers statistics */
    private readonly _dealersEndpoint = `${this._baseUrl}/dealers`;

    /** Endpoint for licenses statistics */
    private readonly _licensesEndpoint = `${this._baseUrl}/licenses`;

    /** Endpoint for users statistics */
    private readonly _usersEndpoint = `${this._baseUrl}/users`;

    /** Endpoint for hosts statistics */
    private readonly _hostsEndpoint = `${this._baseUrl}/hosts`;

    /** Endpoint for contents statistics */
    private readonly _contentsEndpoint = `${this._baseUrl}/contents`;

    /** Endpoint for global statistics */
    private readonly _globalEndpoint = `${this._baseUrl}/global`;

    /** Endpoint for Raspberry Pi version distribution */
    private readonly _raspiEndpoint = `${this._baseUrl}/raspi`;

    /**
     * Retrieves dealers statistics for the dashboard.
     *
     * @returns Observable of dealers statistics
     */
    public getDealers(): Observable<ApiDashboardDealers> {
        return this._http.get<ApiDashboardDealers>(this._dealersEndpoint);
    }

    /**
     * Retrieves licenses statistics for the dashboard.
     *
     * @returns Observable of licenses statistics
     */
    public getLicenses(): Observable<ApiDashboardLicenses> {
        return this._http.get<ApiDashboardLicenses>(this._licensesEndpoint);
    }

    /**
     * Retrieves users statistics for the dashboard.
     *
     * @returns Observable of users statistics
     */
    public getUsers(): Observable<ApiDashboardUsers> {
        return this._http.get<ApiDashboardUsers>(this._usersEndpoint);
    }

    /**
     * Retrieves hosts statistics for the dashboard.
     *
     * @returns Observable of hosts statistics
     */
    public getHosts(): Observable<ApiDashboardHosts> {
        return this._http.get<ApiDashboardHosts>(this._hostsEndpoint);
    }

    /**
     * Retrieves contents statistics for the dashboard.
     *
     * @returns Observable of contents statistics
     */
    public getContents(): Observable<ApiDashboardContents> {
        return this._http.get<ApiDashboardContents>(this._contentsEndpoint);
    }

    /**
     * Retrieves combined global statistics for the dashboard.
     *
     * @returns Observable of global statistics containing all dashboard data
     */
    public getGlobal(): Observable<ApiDashboardGlobal> {
        return this._http.get<ApiDashboardGlobal>(this._globalEndpoint);
    }

    /**
     * Retrieves Raspberry Pi model distribution for the dashboard donut chart.
     *
     * @returns Observable of Pi version buckets
     */
    public getRaspi(): Observable<ApiDashboardRaspi> {
        return this._http.get<ApiDashboardRaspi>(this._raspiEndpoint);
    }
}
